export function tableOfContents() {
  const widgets = [...document.querySelectorAll('[data-toc-widget]')];
  const content = document.querySelector('[data-toc-content]');
  if (!widgets.length || !content) return;

  const headings = [...content.querySelectorAll('h2, h3')].filter((heading) => heading.textContent.trim());
  if (!headings.length) return;

  // Preserve editor anchors and avoid collisions with any other element on the page.
  headings.forEach((heading, index) => {
    const slug = heading.textContent.trim().toLowerCase().normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');
    const base = heading.id || `section-${slug || index + 1}`;
    let id = base;
    let suffix = 2;
    while (document.getElementById(id) && document.getElementById(id) !== heading) {
      id = `${base}-${suffix++}`;
    }
    heading.id = id;
    heading.classList.add('toc-target');
  });

  const wordCount = content.textContent.trim().split(/\s+/u).filter(Boolean).length;
  const readingMinutes = Math.max(1, Math.ceil(wordCount / 200));
  let selectedIndex = null;
  const entries = widgets.map((widget) => {
    const list = widget.querySelector('[data-toc-list]');
    const links = headings.map((heading, index) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      item.className = heading.tagName === 'H3' ? 'widget-toc__subheading' : '';
      link.href = `#${encodeURIComponent(heading.id)}`;
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', (event) => {
        // Keep modified clicks working as normal links.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        selectedIndex = index;
        update();
        window.history.pushState(null, '', link.href);
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        heading.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
      item.appendChild(link);
      list.appendChild(item);
      return link;
    });
    const readingTime = widget.querySelector('[data-toc-reading-time]');
    readingTime.textContent = readingTime.dataset.label.replace('%s', readingMinutes);
    widget.hidden = false;
    return { list, links, progress: widget.querySelector('[data-toc-progress]') };
  });

  let currentIndex = -1;
  let framePending = false;
  function update() {
    framePending = false;
    const offset = parseFloat(window.getComputedStyle(headings[0]).scrollMarginTop) || 150;
    let activeIndex = 0;
    headings.forEach((heading, index) => {
      if (heading.getBoundingClientRect().top <= offset + 1) activeIndex = index;
    });
    // Anchor scrolling can stop short near the page end or land on fractional pixels.
    // Keep the requested section selected until the visitor scrolls manually.
    if (selectedIndex !== null) activeIndex = selectedIndex;

    const rect = content.getBoundingClientRect();
    const distance = Math.max(1, rect.height - window.innerHeight + offset);
    const progress = Math.max(0, Math.min(100, (offset - rect.top) / distance * 100));
    entries.forEach(({ list, links, progress: progressElement }) => {
      progressElement.value = Math.round(progress);
      if (activeIndex !== currentIndex) {
        links.forEach((link, index) => {
          if (index === activeIndex) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      }
      const linkRect = links[activeIndex].getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      list.style.setProperty('--toc-marker-top', `${linkRect.top - listRect.top + list.scrollTop}px`);
      list.style.setProperty('--toc-marker-height', `${linkRect.height}px`);
      if (activeIndex === currentIndex) return;
      if (linkRect.top < listRect.top) list.scrollTop += linkRect.top - listRect.top;
      else if (linkRect.bottom > listRect.bottom) list.scrollTop += linkRect.bottom - listRect.bottom;
    });
    currentIndex = activeIndex;
  }

  function scheduleUpdate() {
    if (framePending) return;
    framePending = true;
    window.requestAnimationFrame(update);
  }
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('load', scheduleUpdate);
  const clearSelection = () => { selectedIndex = null; };
  window.addEventListener('wheel', (event) => {
    if (!event.target?.closest('[data-toc-widget]')) clearSelection();
  }, { passive: true });
  window.addEventListener('touchmove', clearSelection, { passive: true });
  window.addEventListener('pointerdown', clearSelection, { passive: true });
  window.addEventListener('keydown', (event) => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) clearSelection();
  });
  window.addEventListener('popstate', clearSelection);
  window.addEventListener('hashchange', clearSelection);
  update();

  // Newly generated anchors also work when arriving from a copied TOC link.
  let hash;
  try { hash = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
  const target = headings.find((heading) => heading.id === hash);
  if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
}
